import { adminUsersApi } from '@/api/admin/adminUsersApi';
import { UsersTable } from '@/components/admin/users/UsersTable';
import { AdminPager } from '@/components/admin/AdminPager';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import type { AdminUser } from '@/types/admin.model';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 20;

const AdminUsuariosPage = () => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedQuery = useDebouncedValue(query);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    // Two effects can have a fetch in flight at once (a new search resets the page,
    // and both changes fire a load). Only the newest request may write to state:
    // otherwise a slow stale response overwrites the fresh one.
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      const result = await adminUsersApi.listUsers({
        query: debouncedQuery,
        page,
        size: PAGE_SIZE,
      });
      if (current !== requestId.current) return;
      setUsers(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(
        error,
        'No se pudo cargar la lista',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [debouncedQuery, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(debouncedQuery, page, setPage);

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Usuarios</h1>

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Buscar por nombre o email"
        className="max-w-sm"
      />

      <UsersTable users={users} isLoading={isLoading} onActionDone={load} />

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminUsuariosPage;
